/**
 * 反应过程对象 —— 对应技术设计 §6.4。
 *
 * 「加热」「产氧」不是瞬时布尔量，而是有**持续时间和进度**的过程：
 * 温度沿曲线上升、气泡由少到多、集气瓶逐渐变满。§22 红线 4 要求
 * 「实验现象由状态机触发，不能把全流程做成不可交互的视频」——
 * 这个文件就是那个状态机的时间轴。
 *
 * 过程定义来自实验包（`rules.json` 的 `processes` 段），引擎不内置任何具体反应。
 */

export type ProcessState = "idle" | "running" | "paused" | "done" | "aborted";

export interface ProcessDefinition {
  id: string;
  /** 目标状态字段：过程推进时把 progress 映射到某个器材 state 上 */
  drives?: {
    /** `object(<id>).state.<键>` */
    target: string;
    from: number;
    to: number;
  }[];
  /** 从 0 到 1 需要多少毫秒 */
  durationMs: number;
  /** 到 1 之后是否自动停 */
  autoComplete?: boolean;
}

export interface ProcessRuntime {
  state: ProcessState;
  progress?: number;
}

export type ProcessTable = Record<string, ProcessRuntime>;

export function createProcesses(definitions: readonly ProcessDefinition[]): ProcessTable {
  const table: ProcessTable = {};
  for (const definition of definitions) {
    table[definition.id] = { state: "idle", progress: 0 };
  }
  return table;
}

/** 线性推进。曲线形状先按线性 —— 教学上要的是"看得见在变"，不是数值仿真。 */
export function tickProcesses(
  definitions: readonly ProcessDefinition[],
  table: ProcessTable,
  elapsedMs: number,
): { processes: ProcessTable; completed: string[] } {
  let next: ProcessTable = table;
  const completed: string[] = [];

  for (const definition of definitions) {
    const runtime = table[definition.id];
    if (!runtime || runtime.state !== "running") continue;

    const step = definition.durationMs > 0 ? elapsedMs / definition.durationMs : 1;
    const progress = Math.min(1, (runtime.progress ?? 0) + step);
    const done = progress >= 1 && definition.autoComplete !== false;

    next = {
      ...next,
      [definition.id]: { state: done ? "done" : "running", progress },
    };
    if (done) completed.push(definition.id);
  }

  return { processes: next, completed };
}

/**
 * 过程进度 → 器材状态。返回要写的 `set` 列表，由调用方套用到图上，
 * 保持「过程引擎不直接改图」这条边界（改图的入口只有 rule-engine 与 interaction）。
 */
export function derivedStateWrites(
  definitions: readonly ProcessDefinition[],
  table: ProcessTable,
): { set: string; value: number }[] {
  const writes: { set: string; value: number }[] = [];
  for (const definition of definitions) {
    const runtime = table[definition.id];
    if (!runtime || runtime.progress === undefined) continue;

    // **从未启动的过程不写值**。否则 idle + progress 0 会每帧把被驱动的状态
    // 强行按回 `from`，别的来源（规则、交互、教师设定）根本改不动它 ——
    // 表现为「温度怎么都升不上去」，且完全没有报错，极难定位。
    if (runtime.state === "idle") continue;

    for (const drive of definition.drives ?? []) {
      writes.push({
        set: drive.target,
        value: drive.from + (drive.to - drive.from) * runtime.progress,
      });
    }
  }
  return writes;
}

export function startProcess(table: ProcessTable, id: string): ProcessTable {
  const current = table[id] ?? { state: "idle", progress: 0 };
  if (current.state === "running") return table;
  return { ...table, [id]: { ...current, state: "running" } };
}

export function stopProcess(table: ProcessTable, id: string, state: ProcessState = "paused"): ProcessTable {
  const current = table[id];
  if (!current) return table;
  return { ...table, [id]: { ...current, state } };
}
